/**
 * Log Data Sanitization Utility
 * 
 * Prevents sensitive data (PII, credentials, tokens) from being logged.
 * This is critical for production security and compliance.
 */

/**
 * Fields that should be redacted from logs
 * Matches common patterns for sensitive data
 */
const SENSITIVE_FIELDS = [
  'email',
  'password',
  'token',
  'secret',
  'key',
  'auth',
  'session',
  'userId',
  'user_id',
  'workspaceId',
  'workspace_id',
  'anon_key',
  'api_key',
  'access_token',
  'refresh_token',
  'authorization',
  'cookie',
  'credential',
  'private',
  'ssn',
  'social_security',
  'credit_card',
  'card_number',
  'cvv',
  'pin'
];

/**
 * Sanitizes log data by redacting sensitive fields
 * Recursively processes objects and arrays
 * 
 * @param data The data to sanitize
 * @returns Sanitized data with sensitive fields replaced with '[REDACTED]'
 */
export function sanitizeLogData(data: unknown): unknown {
  // Handle null/undefined
  if (data === null || data === undefined) {
    return data;
  }

  // Handle primitives (strings, numbers, booleans)
  if (typeof data !== 'object') {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => sanitizeLogData(item));
  }

  // Handle objects
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase();
    
    // Check if this key matches any sensitive field pattern
    const isSensitive = SENSITIVE_FIELDS.some(field => 
      lowerKey.includes(field.toLowerCase())
    );
    
    if (isSensitive) {
      // Redact sensitive fields
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      // Recursively sanitize nested objects/arrays
      sanitized[key] = sanitizeLogData(value);
    } else {
      // Safe to include as-is
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

/**
 * Sanitizes a string that might contain sensitive data
 * Useful for sanitizing error messages or user input
 * 
 * @param str The string to sanitize
 * @returns Sanitized string
 */
export function sanitizeLogString(str: string): string {
  // Check if string contains patterns that look like sensitive data
  const sensitivePatterns = [
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, // Email addresses
    /Bearer\s+[A-Za-z0-9\-._~+/]+/gi, // Bearer tokens
    /[A-Za-z0-9\-._~+/]{20,}/g, // Long tokens/keys
  ];

  let sanitized = str;
  for (const pattern of sensitivePatterns) {
    sanitized = sanitized.replace(pattern, '[REDACTED]');
  }

  return sanitized;
}

